import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import analyticsApi from '~/apis/analytics';
import supportRequestApi from '~/apis/supportRequest';
import { StatBar } from '~/components/common';
import { IHelpSupportResponse } from '~/utils/interface/analytics';
import { IPaginationResponse } from '~/utils/interface/base';
import { ISupportRequest, SupportRequestStatus } from '~/utils/interface/supportRequest';

// Helper to extract initials from name
const getInitials = (name?: string): string => {
  if (!name) return 'U';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Avatar component with fallback
const CaregiverAvatar = ({
  url,
  name,
}: {
  url?: string | null;
  name?: string;
}) => {
  const { t } = useTranslation();
  const [hasError, setHasError] = useState(false);
  const initials = getInitials(name);

  if (url && !hasError) {
    return (
      <img
        src={url}
        alt={name || t('pages.supportRequests.caregiverAvatarAlt')}
        onError={() => setHasError(true)}
        className='h-9 w-9 shrink-0 rounded-full border border-border object-cover'
      />
    );
  }

  return (
    <div className='flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-primary/20 bg-primary/10 text-xs font-semibold text-primary'>
      {initials}
    </div>
  );
};

// Status Badge Component: only open, resolved, closed
const StatusBadge = ({ status }: { status: SupportRequestStatus }) => {
  const { t } = useTranslation();
  switch (status) {
    case 'open':
      return (
        <span className='inline-flex items-center gap-1.5 rounded-full border border-amber-500/20 bg-amber-500/10 px-2.5 py-0.5 text-xs font-medium text-amber-600'>
          <span className='h-1.5 w-1.5 rounded-full bg-amber-500' />
          {t('pages.supportRequests.statusFilters.open')}
        </span>
      );
    case 'resolved':
      return (
        <span className='inline-flex items-center gap-1.5 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600'>
          <span className='h-1.5 w-1.5 rounded-full bg-emerald-500' />
          {t('pages.supportRequests.statusFilters.resolved')}
        </span>
      );
    case 'closed':
    default:
      return (
        <span className='inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground'>
          <span className='h-1.5 w-1.5 rounded-full bg-muted-foreground' />
          {t('pages.supportRequests.statusFilters.closed')}
        </span>
      );
  }
};

const SupportRequests = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<ISupportRequest[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [stats, setStats] = useState<IHelpSupportResponse>();
  const [isLoading, setIsLoading] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  // Filters & Pagination State
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<'all' | SupportRequestStatus>('all');
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Debounce search input (350ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setPage(1); // Reset to first page on search
    }, 350);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // Load stats once on mount
  useEffect(() => {
    analyticsApi
      .getHelpSupport()
      .then((res) => setStats(res.data.data))
      .catch(() => {});
  }, []);

  // Fetch paginated support requests from server
  const fetchRequests = useCallback(
    async (targetPage = page, targetLimit = pageSize) => {
      setIsTableLoading(true);
      try {
        const res = await supportRequestApi.getSupportRequests({
          page: targetPage,
          limit: targetLimit,
          status: statusFilter === 'all' ? undefined : statusFilter,
          textSearch: debouncedSearch || undefined,
        });

        const resData = res.data.data;
        // Handle both paginated response or legacy flat array gracefully
        if (Array.isArray(resData)) {
          setData(resData);
          setPagination({
            page: 1,
            limit: resData.length,
            totalItems: resData.length,
            totalPages: 1,
          });
        } else if (resData && Array.isArray(resData.responses)) {
          setData(resData.responses);
          if (resData.pagination) {
            setPagination(resData.pagination);
          }
        }
        setError(undefined);
      } catch {
        setError(t('pages.supportRequests.loadError'));
      } finally {
        setIsLoading(false);
        setIsTableLoading(false);
      }
    },
    [page, pageSize, statusFilter, debouncedSearch, t]
  );

  useEffect(() => {
    fetchRequests(page, pageSize);
  }, [fetchRequests, page, pageSize]);

  // Clear feedback after 4s
  useEffect(() => {
    if (!feedbackMessage) return;
    const timer = setTimeout(() => setFeedbackMessage(null), 4000);
    return () => clearTimeout(timer);
  }, [feedbackMessage]);

  const handleStatusChange = async (id: number, newStatus: SupportRequestStatus) => {
    const currentItem = data.find((r) => r.id === id);
    if (!currentItem || currentItem.status === newStatus) return;

    const previousStatus = currentItem.status;
    setUpdatingId(id);

    // Optimistic local update
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, status: newStatus } : item))
    );

    try {
      await supportRequestApi.updateStatus(id, newStatus);
      setFeedbackMessage({
        type: 'success',
        text: t('pages.supportRequests.statusUpdated', {
          id,
          status: t(`pages.supportRequests.statusFilters.${newStatus}`),
        }),
      });
    } catch {
      // Rollback on failure
      setData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: previousStatus } : item))
      );
      setFeedbackMessage({
        type: 'error',
        text: t('pages.supportRequests.statusUpdateError', { id }),
      });
    } finally {
      setUpdatingId(null);
    }
  };

  const handleStatusFilterChange = (newStatus: 'all' | SupportRequestStatus) => {
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > pagination.totalPages) return;
    setPage(newPage);
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  };

  const submittedTotal =
    (stats?.submitted.succeeded ?? 0) + (stats?.submitted.failed ?? 0) || 1;

  const startItem = pagination.totalItems === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endItem = Math.min(pagination.page * pagination.limit, pagination.totalItems);

  return (
    <div>
      <div className='flex flex-col justify-between gap-3 sm:flex-row sm:items-center'>
        <div>
          <h1 className='text-xl font-semibold text-foreground'>{t('pages.supportRequests.title')}</h1>
          <p className='mt-1 text-sm text-muted-foreground'>{t('pages.supportRequests.description')}</p>
        </div>
      </div>

      {feedbackMessage && (
        <div
          className={`mt-4 flex items-center justify-between rounded-lg border px-4 py-2.5 text-sm transition-all ${
            feedbackMessage.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700'
              : 'border-destructive/30 bg-destructive/10 text-destructive'
          }`}
        >
          <span>{feedbackMessage.text}</span>
          <button
            onClick={() => setFeedbackMessage(null)}
            className='text-xs font-semibold underline hover:opacity-80'
          >
            {t('pages.supportRequests.dismiss')}
          </button>
        </div>
      )}

      {isLoading && <p className='mt-6 text-sm text-muted-foreground'>{t('common.loading')}</p>}
      {error && <p className='mt-6 text-sm text-destructive'>{error}</p>}

      {stats && (
        <div className='mt-6 flex flex-col gap-4 rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6'>
          <h2 className='text-base font-semibold text-foreground'>
            {t('pages.supportRequests.helpStatsTitle')}
          </h2>
          <StatBar
            label={t('pages.supportRequests.helpOpened')}
            value={stats.opened}
            total={stats.opened || 1}
          />
          <StatBar
            label={t('pages.supportRequests.requestSucceeded')}
            value={stats.submitted.succeeded}
            total={submittedTotal}
          />
          {stats.submitted.failed > 0 && (
            <StatBar
              label={t('pages.supportRequests.requestFailed')}
              value={stats.submitted.failed}
              total={submittedTotal}
              tone='destructive'
            />
          )}
        </div>
      )}

      <div className='mt-6 rounded-lg border border-border bg-card p-4 shadow-sm sm:p-6'>
        {/* Top Controls: Status Filters & Search */}
        <div className='mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-center'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='text-xs font-medium text-muted-foreground mr-1'>
              {t('pages.supportRequests.status')}
            </span>
            {(['all', 'open', 'resolved', 'closed'] as const).map((st) => (
              <button
                key={st}
                onClick={() => handleStatusFilterChange(st)}
                className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                  statusFilter === st
                    ? 'bg-primary text-primary-foreground'
                    : 'border border-border bg-background text-foreground hover:bg-muted'
                }`}
              >
                {t(`pages.supportRequests.statusFilters.${st}`)}
              </button>
            ))}
          </div>

          <div className='flex items-center gap-2'>
            <div className='relative w-full sm:w-64'>
              <input
                type='text'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('pages.supportRequests.searchPlaceholder')}
                className='w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs text-foreground placeholder:text-muted-foreground focus:border-ring focus:outline-none'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground'
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Content */}
        {isTableLoading && data.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            {t('pages.supportRequests.loadingRequests')}
          </p>
        ) : data.length === 0 ? (
          <p className='py-8 text-center text-sm text-muted-foreground'>
            {t('pages.supportRequests.noResults')}
          </p>
        ) : (
          <div className='relative'>
            {isTableLoading && (
              <div className='absolute inset-0 z-10 flex items-center justify-center bg-card/60 backdrop-blur-[1px]'>
                <span className='rounded bg-card px-3 py-1 text-xs shadow border border-border text-muted-foreground'>
                  {t('pages.supportRequests.updating')}
                </span>
              </div>
            )}
            <div className='overflow-x-auto'>
              <table className='w-full min-w-[760px] text-left text-sm'>
                <thead>
                  <tr className='border-b border-border text-muted-foreground'>
                    <th className='py-3 pr-4 font-medium'>{t('pages.supportRequests.columns.time')}</th>
                    <th className='py-3 pr-4 font-medium'>{t('pages.supportRequests.columns.caregiver')}</th>
                    <th className='py-3 pr-4 font-medium'>{t('pages.supportRequests.columns.subject')}</th>
                    <th className='py-3 pr-4 font-medium min-w-[240px]'>
                      {t('pages.supportRequests.columns.message')}
                    </th>
                    <th className='py-3 pr-4 font-medium'>{t('pages.supportRequests.columns.status')}</th>
                    <th className='py-3 font-medium text-right'>
                      {t('pages.supportRequests.columns.action')}
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border/60'>
                  {data.map((row) => {
                    const userName =
                      row.account?.name ||
                      row.accountName ||
                      (row.account?.firstName && row.account?.lastName
                        ? `${row.account.firstName} ${row.account.lastName}`
                        : undefined) ||
                      t('pages.supportRequests.accountId', { id: row.accountId });

                    const userEmail = row.accountEmail || row.account?.email;
                    const avatarUrl = row.accountAvatar || row.account?.avatar;
                    const isBusy = updatingId === row.id;

                    // Mailto link with prefilled subject and context
                    const mailtoUrl = userEmail
                      ? `mailto:${encodeURIComponent(userEmail)}?subject=${encodeURIComponent(
                          t('pages.supportRequests.emailTemplate.subject', { subject: row.subject })
                        )}&body=${encodeURIComponent(
                          `${t('pages.supportRequests.emailTemplate.greeting', { name: userName })}\n\n${t('pages.supportRequests.emailTemplate.thanks')}\n${t('pages.supportRequests.emailTemplate.regarding', { subject: row.subject })}\n\n\n---\n${t('pages.supportRequests.emailTemplate.originalMessage')}\n${row.message}\n`
                        )}`
                      : undefined;

                    return (
                      <tr key={row.id} className='align-top transition-colors hover:bg-muted/30'>
                        {/* Time */}
                        <td className='py-3.5 pr-4 whitespace-nowrap text-xs text-muted-foreground'>
                          {new Date(row.createdAt).toLocaleString(undefined, {
                            year: 'numeric',
                            month: 'numeric',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            second: '2-digit',
                          })}
                        </td>

                        {/* Caregiver Avatar & Name */}
                        <td className='py-3.5 pr-4'>
                          <div className='flex items-center gap-3'>
                            <CaregiverAvatar url={avatarUrl} name={userName} />
                            <div className='flex flex-col min-w-0'>
                              <span
                                className='font-medium text-foreground text-sm truncate max-w-[150px]'
                                title={userName}
                              >
                                {userName}
                              </span>
                              <span
                                className='text-xs text-muted-foreground truncate max-w-[160px]'
                                title={userEmail || `ID: ${row.accountId}`}
                              >
                                {userEmail || `ID: ${row.accountId}`}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Subject */}
                        <td className='py-3.5 pr-4 font-medium text-foreground max-w-[180px]'>
                          <div className='break-words'>{row.subject}</div>
                        </td>

                        {/* Message */}
                        <td className='py-3.5 pr-4 text-foreground/90 max-w-md'>
                          <p className='whitespace-pre-wrap text-xs leading-relaxed text-foreground/80 break-words'>
                            {row.message}
                          </p>
                        </td>

                        {/* Status Badge */}
                        <td className='py-3.5 pr-4 whitespace-nowrap'>
                          <StatusBadge status={row.status} />
                        </td>

                        {/* Action Column */}
                        <td className='py-3.5 whitespace-nowrap text-right'>
                          <div className='flex items-center justify-end gap-2'>
                            {/* Action 1: Email */}
                            {userEmail ? (
                              <a
                                href={mailtoUrl}
                                className='inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs font-medium text-foreground shadow-sm transition hover:bg-muted hover:border-primary/30 active:scale-95'
                                title={t('pages.supportRequests.sendEmailTitle', { email: userEmail })}
                              >
                                <svg
                                  className='h-3.5 w-3.5 text-muted-foreground'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  strokeWidth='2'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                >
                                  <rect width='20' height='16' x='2' y='4' rx='2' />
                                  <path d='m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L1 7' />
                                </svg>
                                <span>{t('pages.supportRequests.email')}</span>
                              </a>
                            ) : (
                              <button
                                disabled
                                className='inline-flex items-center gap-1.5 rounded-md border border-border/50 bg-muted/40 px-2.5 py-1.5 text-xs font-medium text-muted-foreground opacity-50 cursor-not-allowed'
                                title={t('pages.supportRequests.noEmailTitle')}
                              >
                                <svg
                                  className='h-3.5 w-3.5'
                                  viewBox='0 0 24 24'
                                  fill='none'
                                  stroke='currentColor'
                                  strokeWidth='2'
                                  strokeLinecap='round'
                                  strokeLinejoin='round'
                                >
                                  <rect width='20' height='16' x='2' y='4' rx='2' />
                                  <path d='m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L1 7' />
                                </svg>
                                <span>{t('pages.supportRequests.email')}</span>
                              </button>
                            )}

                            {/* Action 2: Update Status Selector */}
                            <div className='relative inline-block text-left'>
                              <select
                                value={row.status}
                                disabled={isBusy}
                                onChange={(e) =>
                                  handleStatusChange(row.id, e.target.value as SupportRequestStatus)
                                }
                                className={`rounded-md border border-border bg-card px-2 py-1.5 text-xs font-medium text-foreground shadow-sm transition hover:border-primary/30 focus:border-ring focus:outline-none cursor-pointer ${
                                  isBusy ? 'opacity-50 cursor-wait' : ''
                                }`}
                                title={t('pages.supportRequests.updateStatusTitle')}
                              >
                                <option value='open'>
                                  {t('pages.supportRequests.setStatus', {
                                    status: t('pages.supportRequests.statusFilters.open'),
                                  })}
                                </option>
                                <option value='resolved'>
                                  {t('pages.supportRequests.setStatus', {
                                    status: t('pages.supportRequests.statusFilters.resolved'),
                                  })}
                                </option>
                                <option value='closed'>
                                  {t('pages.supportRequests.setStatus', {
                                    status: t('pages.supportRequests.statusFilters.closed'),
                                  })}
                                </option>
                              </select>
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls Footer */}
            <div className='mt-5 flex flex-col items-center justify-between gap-4 border-t border-border/60 pt-4 text-xs text-muted-foreground sm:flex-row'>
              <div>
                {t('pages.supportRequests.pagination.showing', {
                  start: startItem,
                  end: endItem,
                  total: pagination.totalItems,
                })}
              </div>

              <div className='flex items-center gap-4'>
                <div className='flex items-center gap-2'>
                  <span>{t('pages.supportRequests.pagination.rowsPerPage')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className='rounded border border-border bg-background px-2 py-1 text-xs text-foreground focus:outline-none'
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className='flex items-center gap-1'>
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page <= 1 || isTableLoading}
                    className='rounded border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    {t('pages.supportRequests.pagination.previous')}
                  </button>

                  {/* Page indicator pills */}
                  {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                    .filter((p) => {
                      // Show first, last, current, and neighbours
                      return (
                        p === 1 ||
                        p === pagination.totalPages ||
                        Math.abs(p - page) <= 1
                      );
                    })
                    .map((p, idx, arr) => {
                      const prevPage = arr[idx - 1];
                      const showEllipsis = prevPage && p - prevPage > 1;

                      return (
                        <span key={p} className='flex items-center'>
                          {showEllipsis && <span className='px-1 text-muted-foreground'>...</span>}
                          <button
                            onClick={() => handlePageChange(p)}
                            disabled={isTableLoading}
                            className={`min-w-[28px] rounded px-2 py-1 text-xs font-medium transition ${
                              page === p
                                ? 'bg-primary text-primary-foreground font-semibold'
                                : 'border border-border bg-card text-foreground hover:bg-muted'
                            }`}
                          >
                            {p}
                          </button>
                        </span>
                      );
                    })}

                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page >= pagination.totalPages || isTableLoading}
                    className='rounded border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed'
                  >
                    {t('pages.supportRequests.pagination.next')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportRequests;
